import { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import GhlSurvey from "@/components/7fcc/GhlSurvey";
import { scrollToId } from "@/lib/scroll";
import { fireConversion, withAttribution } from "@/lib/tracking";

// Survey-gated VSL (/military). The video is locked until the visitor
// completes the GHL survey. Clicking the frame opens the same GHL survey that
// already feeds the pipeline + workflows (no new form, no GHL changes). When
// the survey reports a submission, the video unlocks and starts with sound.
// Unlock is remembered per browser.
//
// Submission detection: GHL's form_embed iframe posts an array message to the
// parent on submit whose 3rd item is a JSON string of the submitted fields.
// This is GHL behavior, not a documented API — verify on a live deploy.
// Safety valve: a "Done? Watch the video" link appears after FALLBACK_MS so a
// visitor is never stuck if GHL changes that message.
const FALLBACK_MS = 45000;
export const GATE_EVENT = "7fcc:survey-gate";
// The survey's own after-submit redirect (to the booking page) navigates the
// top window from inside the iframe. Leaving out allow-top-navigation blocks
// that for the gate copy only, so the visitor stays and the video unlocks.
const GATE_SANDBOX =
  "allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const readUnlocked = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const isGhlOrigin = (origin = "") =>
  /leadconnectorhq\.com|msgsndr\.com|gohighlevel\.com/.test(origin);

function looksLikeSubmission(data) {
  if (!Array.isArray(data)) return false;
  if (data[0] === "msgsndr-booking-complete") return true;
  const payload = data[2];
  if (typeof payload !== "string") return false;
  return /"email"|"phone"/.test(payload);
}

// Lets other survey slots know the gate modal is open, so the page never
// renders two copies of the same GHL survey iframe at once.
export function useGateOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = (e) => setOpen(Boolean(e.detail?.open));
    window.addEventListener(GATE_EVENT, on);
    return () => window.removeEventListener(GATE_EVENT, on);
  }, []);
  return open;
}

export default function OptInVsl({ src, optin, survey, testId = "optin-vsl" }) {
  const videoRef = useRef(null);
  const [unlocked, setUnlocked] = useState(() => readUnlocked(optin.storageKey));
  const [open, setOpen] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const setModal = (value) => {
    setOpen(value);
    window.dispatchEvent(new CustomEvent(GATE_EVENT, { detail: { open: value } }));
  };

  const unlock = (autoplay) => {
    try {
      localStorage.setItem(optin.storageKey, "1");
    } catch {
      /* storage unavailable */
    }
    setUnlocked(true);
    setModal(false);
    if (autoplay) {
      const v = videoRef.current;
      if (v) {
        v.muted = false;
        v.play().catch(() => {
          // Browser blocked sound without a fresh tap: start muted instead.
          v.muted = true;
          v.play().catch(() => {});
        });
      }
    }
  };

  // When the video finishes, send the visitor to the booking page (same place
  // the survey redirect would have taken them), keeping ad attribution params.
  // No redirect configured → fall back to scrolling to the survey card.
  const onEnded = () => {
    if (!optin.endRedirect) return scrollToId("qualify");
    fireConversion("military_vsl_complete", { source: optin.source });
    const target = new URL(optin.endRedirect, window.location.origin).toString();
    window.location.assign(withAttribution(target));
  };

  // Unlock on a survey submission from either survey on the page.
  useEffect(() => {
    if (unlocked) return undefined;
    const onMessage = (e) => {
      if (!isGhlOrigin(e.origin) || !looksLikeSubmission(e.data)) return;
      fireConversion("military_survey_submit", { source: optin.source });
      unlock(true);
      document.getElementById("vsl")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  useEffect(() => {
    if (!open) return undefined;
    setShowFallback(false);
    const t = setTimeout(() => setShowFallback(true), FALLBACK_MS);
    const onKey = (e) => e.key === "Escape" && setModal(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <div
        className="relative aspect-video w-full overflow-hidden rounded-lg border border-line bg-ink-950 shadow-panel"
        data-testid={testId}
      >
        <video
          ref={videoRef}
          src={src}
          controls={unlocked}
          playsInline
          preload="metadata"
          disablePictureInPicture
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          onEnded={onEnded}
          className="absolute inset-0 h-full w-full object-contain"
        />
        {!unlocked && (
          <button
            type="button"
            onClick={() => setModal(true)}
            aria-label="Unlock the video"
            className="group absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-ink-950/55 backdrop-blur-[2px]"
            data-testid={`${testId}-gate`}
          >
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand text-white shadow-glow-soft transition-transform duration-300 group-hover:scale-105 md:h-24 md:w-24">
              <Play size={34} className="ml-1" fill="currentColor" aria-hidden="true" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-fg">
              Click To Watch
            </span>
          </button>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] overflow-y-auto bg-black/80 px-4 py-6 backdrop-blur-sm md:py-12"
          onMouseDown={(e) => e.target === e.currentTarget && setModal(false)}
          data-testid={`${testId}-modal`}
          data-lenis-prevent
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${testId}-title`}
            className="relative mx-auto w-full max-w-2xl rounded-2xl border border-glowblue bg-ink-950 p-5 shadow-panel md:p-8"
          >
            <button
              type="button"
              onClick={() => setModal(false)}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 text-fg-dim transition-colors hover:text-fg"
            >
              <X size={22} aria-hidden="true" />
            </button>
            <h3
              id={`${testId}-title`}
              className="px-6 text-center font-display text-2xl font-semibold leading-tight text-fg md:text-3xl"
            >
              {optin.title}
            </h3>
            <p className="mt-2 text-center text-sm text-fg-muted">{optin.support}</p>
            <div className="mt-4 md:mt-6">
              <GhlSurvey
                src={survey.src}
                iframeId={survey.iframeId}
                title={survey.title}
                testId={`${testId}-survey`}
                sandbox={GATE_SANDBOX}
              />
            </div>
            {showFallback && (
              <button
                type="button"
                onClick={() => unlock(true)}
                className="mx-auto mt-4 block text-xs uppercase tracking-[0.18em] text-fg-dim underline-offset-4 hover:text-fg hover:underline"
                data-testid={`${testId}-fallback`}
              >
                Done? Watch the video
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
